// Read an uploaded ERC (VN) / ACRA Business Profile (SG) and fill the company
// form from it. Shared by the admin intake page and the borrower KYC page, which
// are on different bundles but do the same three things: upload the file, wait
// for the worker to read it, then fill the fields that are still empty.
//
// Nothing here blocks the form. Every failure path ends in a warning the user
// can ignore and keep typing.

// English fallbacks. Pages that face a borrower pass translated copy in;
// `{document}`, `{file}` and `{count}` are substituted at render time.
const DEFAULT_MESSAGES = {
  waiting: 'Please wait — reading your {document} ({file})...',
  uploadFailed: 'The file could not be uploaded. Please try again.',
  unreadable:
    "{file}: we couldn't read this {document}. Your file is saved — please fill in the details below yourself.",
  nothingToFill:
    '{file}: read, but every field it covers is already filled in.',
  filled:
    '{file}: filled in {count} field(s). Please check them before you submit.',
}

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 180000

export function csrfToken() {
  const input = document.querySelector('input[name=csrfmiddlewaretoken]')
  if (input) return input.value
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : ''
}

// The bytes go straight to S3 and only the key rides along with the form. Both
// submit handlers (admin s3-upload-field, companies_dash s3_uploader) skip an
// input that already carries its key, so uploading here doesn't upload twice.
export async function uploadToS3(input, prefix) {
  const file = input.files[0]
  const params = new URLSearchParams({
    file_name: file.name,
    file_type: file.type,
    prefix: prefix || input.dataset.s3Prefix || '',
  })

  const presign = await fetch(`/uploader/presign_s3/?${params}`)
  if (!presign.ok) throw new Error('Could not prepare the upload')
  const { presigned_url: url, key } = await presign.json()

  const upload = await fetch(url, { method: 'PUT', body: file })
  if (!upload.ok) throw new Error('Could not upload the file')

  return key
}

// Tell the page's own submit handler the file is already in S3.
export function markUploaded(form, input, key) {
  let keyInput = form.querySelector(`input[name="${input.name}_key"]`)
  if (!keyInput) {
    keyInput = document.createElement('input')
    keyInput.type = 'hidden'
    keyInput.name = `${input.name}_key`
    form.appendChild(keyInput)
  }
  keyInput.value = key

  // companies_dash reads this attribute to decide whether to re-upload.
  input.setAttribute('data-file', JSON.stringify({ key, is_uploaded: true }))

  // Cleared rather than disabled: both submit handlers skip a file input with no
  // selection, and the field stays usable so a wrong file can be replaced.
  input.value = ''
}

export async function requestParse(parseUrl, key, country, companyId) {
  const body = new URLSearchParams({
    key,
    country: country || '',
    company_id: companyId || '',
  })
  const response = await fetch(parseUrl, {
    method: 'POST',
    headers: {
      'X-CSRFToken': csrfToken(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  })

  const payload = await response.json().catch(() => ({}))
  if (payload.status !== 'queued') {
    // "unsupported" and friends come back straight away, with their own message.
    return payload
  }

  const result = await pollForResult(payload.poll_url)
  // Only staff pages ask for the technical cause; a borrower sees the plain
  // message alone.
  return { ...result, showDetail: payload.show_detail === true }
}

async function pollForResult(pollUrl) {
  const deadline = Date.now() + POLL_TIMEOUT_MS

  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS)

    const response = await fetch(pollUrl)
    if (!response.ok) {
      return {
        status: 'error',
        message: 'Lost track of the document read. Try uploading it again.',
        detail: `Polling ${pollUrl} returned ${response.status}.`,
      }
    }

    const payload = await response.json()
    if (payload.status === 'done')
      return payload.value || { status: 'empty', fields: {} }
    if (payload.status === 'error') {
      // The job itself blew up, so the worker's own error is the useful part.
      return {
        status: 'error',
        message: 'The document could not be read.',
        detail: payload.error || '',
      }
    }
  }

  return {
    status: 'error',
    message:
      'Reading the document is taking too long. Fill the form in by hand.',
  }
}

// Only blanks are filled: a value already typed is the operator's, and a parse
// must never overwrite it.
export function applyFields(fields, resolveField, dateFormat) {
  const filled = []

  Object.entries(fields || {}).forEach(([name, value]) => {
    const element = resolveField(name)
    if (!element || element.value) return

    element.value = name.endsWith('_date_of_issue')
      ? formatDate(value, dateFormat)
      : value
    element.dispatchEvent(new Event('change', { bubbles: true }))
    element.classList.add('prefilled-from-document')
    filled.push(name)
  })

  return filled
}

function formatDate(isoValue, dateFormat) {
  if (dateFormat !== 'dmy') return isoValue
  const [year, month, day] = String(isoValue).split('-')
  return year && month && day ? `${day}/${month}/${year}` : isoValue
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// One upload -> parse -> fill cycle, driven from whichever page the file input
// lives on. `statusEl` is the only thing it renders into; the caller decides
// where that sits.
export async function runPrefill({
  input,
  form,
  parseUrl,
  statusEl,
  documentName,
  dateFormat,
  fieldPrefix,
  messages,
}) {
  if (!input.files.length) return

  const text = { ...DEFAULT_MESSAGES, ...(messages || {}) }
  const fileName = input.files[0].name
  const setStatus = (html) => {
    if (statusEl) statusEl.innerHTML = html
  }

  const fill = (template, values) =>
    Object.entries({
      document: documentName,
      file: fileName,
      ...values,
    }).reduce(
      (out, [key, value]) => out.split(`{${key}}`).join(value),
      template,
    )

  setStatus(spinnerHtml(fill(text.waiting)))

  let key
  try {
    key = await uploadToS3(input, 'business_registrations/')
    markUploaded(form, input, key)
  } catch (error) {
    console.error(error)
    setStatus(noticeHtml('warn', fill(text.uploadFailed)))
    return
  }

  let result
  try {
    result = await requestParse(parseUrl, key, '')
  } catch (error) {
    console.error(error)
    result = { status: 'error' }
  }

  if (result.status !== 'ok') {
    // The server's own message says what went wrong with THIS file; the generic
    // copy is only the fallback for when it didn't say.
    const reason = result.message || fill(text.unreadable)
    setStatus(noticeHtml('warn', `${fileName}: ${reason}`, detailOf(result)))
    return
  }

  const resolveField = (name) =>
    form.querySelector(`#id_${fieldPrefix || ''}${name}`) ||
    form.querySelector(`[name="${fieldPrefix || ''}${name}"]`)

  const filled = applyFields(result.fields, resolveField, dateFormat)
  if (!filled.length) {
    setStatus(noticeHtml('ok', fill(text.nothingToFill)))
    return
  }

  setStatus(noticeHtml('ok', fill(text.filled, { count: filled.length })))
}

export function spinnerHtml(message) {
  return `
    <div class="doc-parse-status doc-parse-status--busy">
      <span class="doc-parse-spinner" aria-hidden="true"></span>
      <span>${escapeHtml(message)}</span>
    </div>`
}

// `detail` is the technical cause. It is rendered small and secondary, and only
// when the page asked for it -- a borrower gets the message alone.
export function noticeHtml(level, message, detail) {
  const note = detail
    ? `<div class="doc-parse-detail">${escapeHtml(detail)}</div>`
    : ''
  return `<div class="doc-parse-status doc-parse-status--${level}">${escapeHtml(message)}${note}</div>`
}

export function detailOf(result) {
  return result && result.showDetail ? result.detail || '' : ''
}

export function escapeHtml(value) {
  const node = document.createElement('span')
  node.textContent = value == null ? '' : String(value)
  return node.innerHTML
}
